import React, { useMemo } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "@common/types/integrationConnection";
import { INTEGRATION_CONNECTIONS_SETTINGS } from "@common/helpers/integrationConnection";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { timeAgo } from "@helpers/date";
import { AlertIcon, ClockIcon } from "@icons";
import { DisplayIntegrationAccount } from "../../../integrationAccount/DisplayIntegrationAccount";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { CONNECTION_PROVIDER_INSTITUTIONS } from "@components/institution/resolveInstitutionIcon";
import { NewIntegrationConnection } from "../../../integrationConnection/NewIntegrationConnection";
import css from "./ProviderIntegrationConnections.module.scss";

interface Props {
    integrationConnections: IntegrationConnection[];
    provider: ConnectionProvider;
}

export const SingleAccountConnections: React.FC<Props> = ({
    integrationConnections,
    provider,
}) => {
    const settings = INTEGRATION_CONNECTIONS_SETTINGS[provider];

    const mostRecentBalanceSyncedAt = useMemo(() => {
        if (!integrationConnections?.length) {
            return null;
        }

        const dates = integrationConnections
            .map((conn) => conn.integrationAccounts?.[0]?.balanceSyncedAt)
            .filter((date): date is Date => date instanceof Date);

        if (!dates.length) {
            return null;
        }

        return dates.reduce(
            (latest, current) =>
                current.getTime() > latest.getTime() ? current : latest,
            dates[0],
        );
    }, [integrationConnections]);

    const hasConnectionError = integrationConnections.some(
        (connection) => connection.error,
    );

    return (
        <Collapsible
            className={css.container}
            header={
                <div className={css.header}>
                    <InstitutionIcon
                        institution={CONNECTION_PROVIDER_INSTITUTIONS[provider]}
                        size="lg"
                    />
                    <main>
                        <h5>
                            {settings.label}
                            {hasConnectionError && (
                                <Tooltip content="One or more accounts are disconnected.">
                                    <span>
                                        <AlertIcon className="ml-2 icon-color-red-500" />
                                    </span>
                                </Tooltip>
                            )}
                        </h5>
                        {mostRecentBalanceSyncedAt && (
                            <small>
                                <ClockIcon
                                    height={12}
                                    width={12}
                                    className="icon-color-current"
                                />
                                {timeAgo(mostRecentBalanceSyncedAt)}
                            </small>
                        )}
                    </main>
                </div>
            }
            footer={
                <div className={css.footerSection}>
                    <NewIntegrationConnection
                        provider={provider}
                        btnVariant="primary"
                    >
                        Add another account
                    </NewIntegrationConnection>
                </div>
            }
        >
            <>
                {integrationConnections?.map((connection) => {
                    if (!connection.integrationAccounts?.length) {
                        return null;
                    }
                    return (
                        <DisplayIntegrationAccount
                            key={connection.id}
                            className={css.displayAccount}
                            connection={connection}
                            integrationAccount={
                                connection.integrationAccounts[0]
                            }
                        />
                    );
                })}
            </>
        </Collapsible>
    );
};
